import { Health, SleepSession } from "capacitor-sleep";
import { Strings } from "../../constants/Strings";
import { TimePeriod } from "./AccountHttpService";
import { ConnectionType } from "../../types/AccountSettings";
import { HealthConnectData, HealthConnectSleepEpisode } from "../../types/HealthConnectData";
import { LocalHealthData } from "../../types/LocalHealthData";
import { SleepStage, SleepStageType } from "../../types/SleepLog";
import SleepStagesUtils from "../../utils/SleepStagesUtils";
import { DateTime } from "luxon";

export class LocalConnectionSynchronizer {
    constructor(private readonly connections: ConnectionType[]) {}

    async fetchSleepData(syncPeriod: TimePeriod) {
        const groupedSleepData: LocalHealthData = {
        };

        for (const connection of this.connections) {
            if (connection === "healthConnect") {
                try {
                    const availability = await Health.isHealthAvailable();
                    if (!availability.available) {
                        continue;
                    }

                    const sleepEpisodes = await this.fetchHealthConnectData(syncPeriod);
                    groupedSleepData[connection] = sleepEpisodes;
                } catch (error: any) {
                    groupedSleepData[connection] = { 
                        error: {
                            code: error.name ?? "SyncFailed",
                            message: error.message
                        }
                    };
                }
            }
        }

        return groupedSleepData;
    }

    /**
     * Fetch sleep data for a specific local calendar date (YYYY-MM-DD).
     * The date window is [date 00:00:00, next day 00:00:00) in local time.
     * Mirrors error handling of fetchSleepData.
     */
    async fetchSleepDataForDate(dateYYYYMMDD: string): Promise<LocalHealthData> {
        const groupedSleepData: LocalHealthData = {};

        for (const connection of this.connections) {
            if (connection === "healthConnect") {
                try {
                    const availability = await Health.isHealthAvailable();
                    if (!availability.available) {
                        continue;
                    }
                    const sleepEpisodes = await this.fetchHealthConnectDataForDate(dateYYYYMMDD);
                    groupedSleepData[connection] = sleepEpisodes;
                } catch (error: any) {
                    groupedSleepData[connection] = {
                        error: {
                            code: error.name ?? "SyncFailed",
                            message: error.message
                        }
                    };
                }
            }
        }

        return groupedSleepData;
    }

    private async fetchHealthConnectData(syncPeriod: TimePeriod): Promise<HealthConnectData> {
        const availability = await Health.isHealthAvailable();
        if (!availability.available) {
            throw new Error(Strings.HealthConnectUnavailable);
        }

        // Check if we have the necessary permissions before fetching data
        const permissionCheck = await Health.checkHealthPermissions({
            permissions: ['READ_SLEEP']
        });

        const granted = permissionCheck.permissions;
        const sleepGranted = !!granted.READ_SLEEP;

        if (!sleepGranted) {
            throw new Error(Strings.HealthConnectPermissionsNotGranted);
        }

        const endDt = DateTime.utc();
        let startDt = endDt;

        let desiredDays: number;
        switch (syncPeriod) {
            case 'latest':
                desiredDays = 2; // small window for recent nights
                break;
            case 'lastWeek':
                desiredDays = 7;
                break;
            case 'lastMonth':
                desiredDays = 30;
                break;
            case 'lastYear':
            case 'all':
                desiredDays = 30; // cap enforced
                break;
            default:
                desiredDays = 7; // fallback sensible default
                break;
        }

        // Enforce maximum of 30 days
        const maxDays = 30;
        if (desiredDays > maxDays) {
            desiredDays = maxDays;
        }
        startDt = endDt.minus({ days: desiredDays });

        const response = await Health.querySleepSessions({
            startDate: startDt.toISO(),
            endDate: endDt.toISO(),
            includeStages: true,
        });

        const sleepEpisodes = response.sleepSessions.map(session => convertToSleepEpisode(session));

        return {
            sleepEpisodes
        };   
    }

    /**
     * Fetch Health Connect sleep data strictly for one local calendar date.
     * @param dateYYYYMMDD Date string in format YYYY-MM-DD
     */
    private async fetchHealthConnectDataForDate(dateYYYYMMDD: string): Promise<HealthConnectData> {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateYYYYMMDD)) {
            throw new Error("InvalidDateFormat");
        }

        const availability = await Health.isHealthAvailable();
        if (!availability.available) {
            throw new Error(Strings.HealthConnectUnavailable);
        }

        const permissionCheck = await Health.checkHealthPermissions({
            permissions: ['READ_SLEEP']
        });
        const granted = permissionCheck.permissions;
        const sleepGranted = !!granted.READ_SLEEP;
        if (!sleepGranted) {
            throw new Error(Strings.HealthConnectPermissionsNotGranted);
        }

        const [year, month, day] = dateYYYYMMDD.split('-').map(Number);
        const baseUtc = DateTime.utc(year, month, day, 0, 0, 0, 0);
        const minUtcStart = baseUtc.minus({ hours: 14 });
        const maxUtcEnd = baseUtc.plus({ days: 1, hours: 12 });

        const response = await Health.querySleepSessions({
            startDate: minUtcStart.toISO(),
            endDate: maxUtcEnd.toISO(),
            includeStages: true,
        });

        console.log(`Health Connect sleep sessions response for ${dateYYYYMMDD}:`, response);

        const allEpisodes = response.sleepSessions.map(session => convertToSleepEpisode(session));
        const sleepEpisodes = allEpisodes.filter(ep => this.dateFromEpisode(ep) === dateYYYYMMDD);
        return { sleepEpisodes };
    }

    private dateFromEpisode(ep: HealthConnectSleepEpisode): string {
        const zoneName = (ep.timezone && ep.timezone.startsWith('UTC'))
            ? ep.timezone
            : convertFixedTimezoneToLuxonZone(ep.timezone) ?? 'UTC';
        const end = DateTime.fromISO(ep.end, { setZone: true }).setZone(zoneName);
        return end.toISODate();
    }
}

// Helper function to convert Health Connect sleep session to SleepStagesEpisode format
function convertToSleepEpisode(session: SleepSession): HealthConnectSleepEpisode {
    const sleepStages: SleepStage[] = [];
    let totalAwakeSeconds = 0;
    let totalAsleepSeconds = 0;

    /*
        Format of input:
        Z - for UTC (ISO-8601)
        +hh:mm or -hh:mm - if the seconds are zero (ISO-8601)
        +hh:mm:ss or -hh:mm:ss - if the seconds are non-zero (not ISO-8601) 
    */
    

    if (session.stages && session.stages.length > 0) {
        for (const stage of session.stages) {
            const startDt = DateTime.fromISO(stage.startDate);
            const endDt = DateTime.fromISO(stage.endDate);
            const seconds = endDt.diff(startDt, 'seconds').seconds;
            
            let stageType: SleepStageType;
            switch (stage.stage) {
                case 'AWAKE':
                case 'OUT_OF_BED':
                    stageType = 'awake';
                    totalAwakeSeconds += seconds;
                    break;
                case 'DEEP':
                    stageType = 'deep';
                    totalAsleepSeconds += seconds;
                    break;
                case 'LIGHT':
                case 'SLEEPING':
                    stageType = 'light';
                    totalAsleepSeconds += seconds;
                    break;
                case 'REM':
                    stageType = 'rem';
                    totalAsleepSeconds += seconds;
                    break;
                default:
                    stageType = 'asleep';
                    totalAsleepSeconds += seconds;
                    break;
            }

            sleepStages.push({
                type: stageType,
                start: stage.startDate,
                seconds: Math.round(seconds)
            });
        }
    } else {
        // If no stages available, create a simple asleep stage
        const seconds = session.duration;
        totalAsleepSeconds = seconds;
        sleepStages.push({
            type: 'asleep',
            start: session.startDate,
            seconds: Math.round(seconds)
        });
    }

    const sleepEpisode: HealthConnectSleepEpisode = {
        source: session.sourceBundleId,
        type: 'stages',
        start: session.startDate,
        timezone: convertFixedTimezoneToLuxonZone(session.startTimeZone),
        end: session.endDate,
        isMainSleep: true,
        minutesAsleep: Math.round(totalAsleepSeconds / 60),
        minutesAwake: Math.round(totalAwakeSeconds / 60),
        sleepStages: SleepStagesUtils.compress(sleepStages)
    };

    return sleepEpisode;
}

// Map Kotlin/Java ZoneOffset.id strings to Luxon zone names (ignore seconds entirely)
// Examples:
//   'Z' -> 'UTC'
//   '+02:00' -> 'UTC+02:00'
//   '-07:00' -> 'UTC-07:00'
//   '+05:30:45' -> 'UTC+05:30' (seconds ignored)
function convertFixedTimezoneToLuxonZone(zoneId?: string): string | undefined {
    if (!zoneId) return undefined;
    if (zoneId === 'Z') return 'UTC';
    const m = /^([+-])(\d{2}):(\d{2})(?::\d{2})?$/.exec(zoneId);
    if (!m) return undefined;
    const sign = m[1];
    const hh = m[2];
    const mm = m[3];
    return `UTC${sign}${hh}:${mm}`;
}

// Removed older offset helpers; not needed with episode-based date computation